/**
 * Motor genérico dos catálogos de filtros das telas de consulta.
 *
 * Cada tela declara apenas a lista de campos e recebe daqui tudo o que a tela precisa: o painel
 * monta os campos a partir do catálogo, a barra de chips descreve o que está ativo lendo dele, e o
 * `getParams` monta a query string dele. Adicionar um filtro é acrescentar uma entrada na lista da
 * tela (e o `where` correspondente no controller).
 *
 * Campo:
 *   chave        — nome interno (e o parâmetro na API, salvo se `param` disser outro)
 *   titulo       — rótulo no painel e no chip
 *   grupo        — seção do painel
 *   tipo         — 'multi' | 'select' | 'data' | 'numero' (default)
 *   cols         — largura em colunas do grid (sm)
 *   opcoes       — [{ value, text }] para 'multi' e 'select'
 *   dica         — hint do campo
 *   prefixo/sufixo — adornos do 'numero'
 *   padrao       — valor inicial; um campo no padrão NÃO restringe nada e NÃO vira chip
 *   sempreEnviar — manda o parâmetro mesmo no padrão (quando o backend precisa saber a escolha)
 */
export function criarCatalogoFiltros(CAMPOS) {
    const GRUPOS = CAMPOS.reduce((acc, campo) => {
        if (!acc.includes(campo.grupo)) {
            acc.push(campo.grupo);
        }
        return acc;
    }, []);

    function campo(chave) {
        return CAMPOS.find(c => c.chave === chave);
    }

    function camposDoGrupo(grupo) {
        return CAMPOS.filter(c => c.grupo === grupo);
    }

    function valorPadrao(c) {
        if (c.padrao !== undefined) {
            return c.padrao;
        }

        return c.tipo === 'multi' ? [] : '';
    }

    function textoOpcao(c, valor) {
        const opcao = (c.opcoes || []).find(o => o.value === valor);
        return opcao ? opcao.text : valor;
    }

    function rotulo(chave, valor) {
        if (valor === null || valor === undefined || valor === '') {
            return '';
        }

        const c = campo(chave);

        if (!c || !c.opcoes) {
            return valor;
        }

        return textoOpcao(c, valor);
    }

    function filtrosPadrao() {
        return CAMPOS.reduce((acc, c) => {
            const padrao = valorPadrao(c);
            acc[c.chave] = Array.isArray(padrao) ? [...padrao] : padrao;
            return acc;
        }, {});
    }

    function vazio(c, valor) {
        const padrao = valorPadrao(c);

        if (c.tipo === 'multi') {
            if (!Array.isArray(valor) || !valor.length) {
                return true;
            }

            return JSON.stringify([...valor].sort()) === JSON.stringify([...padrao].sort());
        }

        if (valor === '' || valor === null || valor === undefined) {
            return true;
        }

        return valor === padrao;
    }

    function normalizarFiltros(filtros) {
        const base = filtrosPadrao();
        const origem = filtros || {};

        Object.keys(base).forEach(chave => {
            if (origem[chave] === undefined) {
                return;
            }

            const c = campo(chave);

            base[chave] = c.tipo === 'multi'
                ? (Array.isArray(origem[chave]) ? origem[chave] : [])
                : origem[chave];
        });

        return base;
    }

    function totalAtivos(filtros) {
        return CAMPOS.filter(c => !vazio(c, (filtros || {})[c.chave])).length;
    }

    /**
     * Descrição legível de cada filtro ativo, para a barra de chips.
     */
    function chipsAtivos(filtros) {
        return CAMPOS
            .filter(c => !vazio(c, (filtros || {})[c.chave]))
            .map(c => {
                const valor = filtros[c.chave];
                let texto;

                if (c.tipo === 'multi') {
                    texto = valor.map(v => textoOpcao(c, v)).join(', ');
                } else if (c.tipo === 'select') {
                    texto = textoOpcao(c, valor);
                } else if (c.tipo === 'data') {
                    texto = valor.split('-').reverse().join('/');
                } else {
                    texto = c.sufixo ? `${valor} ${c.sufixo}` : `${valor}`;
                    texto = c.prefixo ? `${c.prefixo} ${texto}` : texto;
                }

                return { chave: c.chave, titulo: c.titulo, texto };
            });
    }

    function aplicarParams(params, filtros) {
        CAMPOS.forEach(c => {
            let valor = (filtros || {})[c.chave];

            if (vazio(c, valor)) {
                if (!c.sempreEnviar) {
                    return;
                }

                valor = valorPadrao(c);

                if (valor === '' || valor === null || valor === undefined) {
                    return;
                }
            }

            const nome = c.param || c.chave;

            if (c.tipo === 'multi') {
                valor.forEach(v => params.append(`${nome}[]`, v));
            } else {
                params.append(nome, valor);
            }
        });

        return params;
    }

    return {
        CAMPOS,
        GRUPOS,
        campo,
        camposDoGrupo,
        rotulo,
        filtrosPadrao,
        normalizarFiltros,
        totalAtivos,
        chipsAtivos,
        aplicarParams,
    };
}
